import { useEffect, useState } from "react"
import { useRouter } from "next/router"

import { useOrganization, useOrganizationList } from "@clerk/nextjs"
import type { OrganizationResource } from "@clerk/shared/types"

import { logger } from "@/lib/logger"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

const log = logger("useOrganizationMembers")

const MEMBERSHIP_PAGE_SIZE = 100

export interface OrganizationMember {
  id: string
  email: string
  name: string
  imageUrl?: string
}

interface UseOrganizationMembersResult {
  members: OrganizationMember[]
  isLoading: boolean
}

/**
 * Pages an organization's memberships to completion. `name` falls back to the
 * identifier when a member has no first/last name set, so a row always has
 * something to render.
 */
async function loadOrganizationMembers(
  organization: OrganizationResource,
): Promise<OrganizationMember[]> {
  const byUserId = new Map<string, OrganizationMember>()
  let currentPage = 1
  let hasMore = true

  while (hasMore) {
    const response = await organization.getMemberships({
      pageSize: MEMBERSHIP_PAGE_SIZE,
      initialPage: currentPage,
    })

    for (const membership of response.data) {
      const member = membership.publicUserData
      if (!member?.userId || !member?.identifier) {
        continue
      }
      byUserId.set(member.userId, {
        id: member.userId,
        email: member.identifier,
        name:
          member.firstName && member.lastName
            ? `${member.firstName} ${member.lastName}`
            : member.identifier,
        imageUrl: member.imageUrl,
      })
    }

    hasMore = currentPage * MEMBERSHIP_PAGE_SIZE < response.total_count
    currentPage++
  }

  return Array.from(byUserId.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  )
}

const inFlightLoads = new Map<string, Promise<OrganizationMember[]>>()

/**
 * Shares one roster load between every caller that mounts while it is in
 * flight, so a page of review controls — each resolving its own reviewers —
 * pages the memberships once rather than once per control.
 */
function loadOrganizationMembersShared(
  organization: OrganizationResource,
): Promise<OrganizationMember[]> {
  const inFlight = inFlightLoads.get(organization.id)
  if (inFlight) {
    return inFlight
  }
  const load = loadOrganizationMembers(organization).finally(() => {
    inFlightLoads.delete(organization.id)
  })
  inFlightLoads.set(organization.id, load)
  return load
}

/**
 * Members of `organization`, refetched whenever it changes. `isResolved` is
 * false while the caller is still working out which organization it means; the
 * roster stays empty and loading until then. A resolved `null` is "no
 * organization", which is empty and not loading.
 */
function useMembersOf(
  organization: OrganizationResource | null | undefined,
  isResolved: boolean,
): UseOrganizationMembersResult {
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    if (!isResolved) {
      setMembers([])
      setIsLoading(true)
      return
    }

    if (!organization) {
      setMembers([])
      setIsLoading(false)
      return
    }

    // Drop the previous organization's roster at once rather than showing it
    // under the new one while this one loads.
    setMembers([])
    setIsLoading(true)
    loadOrganizationMembersShared(organization)
      .then((loaded) => {
        if (!cancelled) {
          setMembers(loaded)
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          log.error("Failed to load organization members", error)
          setMembers([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isResolved, organization])

  return { members, isLoading }
}

/**
 * Every member of the active Clerk organization, paged to completion.
 *
 * Clerk returns memberships a page at a time, so callers that need the whole
 * roster to populate a picker would otherwise each reimplement the paging loop.
 */
export function useOrganizationMembers(): UseOrganizationMembersResult {
  const { isLoaded, organization } = useOrganization()
  return useMembersOf(organization, isLoaded)
}

/**
 * Every member of the organization this tab's URL names, paged to completion.
 *
 * Clerk's active organization drifts to whichever tab last had focus, so it is
 * used only when it is the route's organization. Otherwise the route's
 * organization is found among the user's own memberships. A route naming an
 * organization the user does not belong to yields no members, never another
 * organization's. A route naming no organization falls back to the active one.
 */
export function useRouteOrganizationMembers(): UseOrganizationMembersResult {
  const router = useRouter()
  const routeOrganizationSlug = getRouteOrganizationSlug(router.asPath)
  const { isLoaded, organization: activeOrganization } = useOrganization()
  const { isLoaded: isListLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: MEMBERSHIP_PAGE_SIZE },
  })

  const activeMatchesRoute =
    Boolean(routeOrganizationSlug) &&
    activeOrganization?.slug === routeOrganizationSlug
  const membershipOrganization = routeOrganizationSlug
    ? userMemberships?.data?.find(
        (membership) => membership.organization.slug === routeOrganizationSlug,
      )?.organization
    : undefined
  const needsAnotherMembershipPage =
    Boolean(routeOrganizationSlug) &&
    !activeMatchesRoute &&
    !membershipOrganization &&
    Boolean(userMemberships?.hasNextPage)

  useEffect(() => {
    if (needsAnotherMembershipPage && !userMemberships?.isFetching) {
      void Promise.resolve(userMemberships?.fetchNext?.()).catch(
        (error: unknown) => {
          log.warn("Failed to load more organization memberships", { error })
        },
      )
    }
  }, [needsAnotherMembershipPage, userMemberships])

  const targetOrganization = !routeOrganizationSlug
    ? activeOrganization
    : activeMatchesRoute
      ? activeOrganization
      : (membershipOrganization ?? null)
  const isResolved = !routeOrganizationSlug
    ? isLoaded
    : activeMatchesRoute ||
      Boolean(membershipOrganization) ||
      (isListLoaded &&
        userMemberships?.data !== undefined &&
        !userMemberships.isFetching &&
        !userMemberships.hasNextPage)

  return useMembersOf(targetOrganization, isResolved)
}
